import React, { useState } from 'react';
import cc from 'classcat';

import styles from './styles.module.scss';

interface FaqItem {
  question: string;
  answer: string;
}

interface FaqCategory {
  title: string;
  items: FaqItem[];
}

// CMS Matrix block type shape (Table field returns named columns)
interface CmsFaqCategory {
  categoryTitle?: string | null;
  questions?: Array<{ question: string; answer: string }> | null;
}

interface Props {
  categories?: CmsFaqCategory[] | null;
}

/** Map CMS data to our internal format */
function mapCmsCategories(categories: CmsFaqCategory[]): FaqCategory[] {
  return categories
    .filter((cat) => cat.categoryTitle && cat.questions?.length)
    .map((cat) => ({
      title: cat.categoryTitle!,
      items: (cat.questions || []).map((q) => ({
        question: q.question,
        answer: q.answer,
      })),
    }));
}

/** Level 2: Individual Q&A accordion */
const FaqAccordionItem = ({ question, answer }: FaqItem) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={cc({ [styles.accordionItem]: true, [styles.open]: isOpen })}>
      <button
        className={styles.question}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        type="button"
      >
        <span>{question}</span>
        <span className={styles.questionIcon}>{isOpen ? '\u2212' : '+'}</span>
      </button>
      <div className={styles.answerWrapper}>
        <div className={styles.answer}>
          <p>{answer}</p>
        </div>
      </div>
    </div>
  );
};

/** Level 1: Category accordion that reveals questions */
const FaqCategoryAccordion = ({ title, items }: FaqCategory) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className={cc({ [styles.category]: true, [styles.categoryOpen]: isOpen })}>
      <button
        className={styles.categoryTitle}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        type="button"
      >
        <span>{title}</span>
        <span className={styles.categoryIcon}>{isOpen ? '\u2212' : '+'}</span>
      </button>
      <div className={styles.categoryContentWrapper}>
        <div className={styles.categoryContent}>
          {items.map((item) => (
            <FaqAccordionItem key={item.question} {...item} />
          ))}
        </div>
      </div>
    </div>
  );
};

const MenorahFaq = ({ categories }: Props) => {
  const faqData = categories ? mapCmsCategories(categories) : [];

  if (faqData.length === 0) return null;

  return (
    <div className={styles.faq}>
      {faqData.map((category) => (
        <FaqCategoryAccordion key={category.title} {...category} />
      ))}
    </div>
  );
};

export default MenorahFaq;
